import isEqual from "lodash/isEqual";

import {
  CONVENTIONS,
  type ConventionId,
  type PointCloud,
  type PointCloudAxis,
} from "@/shared/state/visualization/schema/v1/three-d";

import { metreConventionName, type AxisPresetName } from "./axisPresetNames";

export type PointCloudPresetId = "ros_body" | "enu" | "ned" | "rdf";

/** The source → scene axis mapping of a point cloud: the half of its spec a preset fills in. */
export type PointCloudAxes = Pick<PointCloud, "right" | "forward" | "up">;

/**
 * A named axis convention for a point cloud.
 *
 * Foxglove data is right-handed and Babylon's scene is left-handed Y-up, so
 * drawing one in the other takes exactly one axis reflection; every preset is a
 * reflection, which is what makes a cloud look the same here as in Foxglove.
 * The axes come from the shared convention catalogue, so a cloud lands where a
 * position or a rotation in the same convention does.
 */
export interface PointCloudPreset {
  id: PointCloudPresetId;
  /** Named as the position picker names the same convention, units and all. */
  name: AxisPresetName;
  /** The catalogue convention this preset's axes are taken from. */
  convention: ConventionId;
  right: PointCloudAxis;
  forward: PointCloudAxis;
  up: PointCloudAxis;
}

function presetFromConvention(
  id: PointCloudPresetId,
  convention: ConventionId,
): PointCloudPreset {
  const { right, forward, up } = CONVENTIONS[convention];
  return {
    id,
    name: metreConventionName(convention),
    convention,
    right: { ...right },
    forward: { ...forward },
    up: { ...up },
  };
}

export const POINT_CLOUD_PRESETS: readonly PointCloudPreset[] = [
  // REP-103: x forward, y left, z up — the frame a lidar or depth cloud is
  // published in, and ROS's world frames such as odom and map.
  presetFromConvention("ros_body", "flu"),
  presetFromConvention("enu", "enu"),
  // PX4's local frame and ROS's `_ned` frames.
  presetFromConvention("ned", "ned"),
  // ROS's `_optical` frames: what a depth camera's own cloud is published in.
  presetFromConvention("rdf", "rdf"),
];

export const DEFAULT_POINT_CLOUD_PRESET: PointCloudPreset =
  POINT_CLOUD_PRESETS[0];

/** The axes a preset stands for, in the shape the spec stores them. */
export function axesOf(preset: PointCloudPreset): PointCloudAxes {
  return { right: preset.right, forward: preset.forward, up: preset.up };
}

/** The preset matching stored axes exactly, or "custom" when none does. */
export function presetIdFor(
  axes: PointCloudAxes,
): PointCloudPresetId | "custom" {
  const match = POINT_CLOUD_PRESETS.find((preset) =>
    isEqual(axesOf(preset), {
      right: axes.right,
      forward: axes.forward,
      up: axes.up,
    }),
  );
  return match?.id ?? "custom";
}
